import { useEffect, useState } from 'react'
import { isIosDevice, isStandaloneMode } from '../lib/pwa/environment'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

export function usePwaInstall() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [standalone, setStandalone] = useState(isStandaloneMode)

  useEffect(() => {
    const displayMode = window.matchMedia('(display-mode: standalone)')
    const onInstallPrompt = (event: Event) => { event.preventDefault(); setInstallPrompt(event as BeforeInstallPromptEvent) }
    const onInstalled = () => { setInstallPrompt(null); setStandalone(true) }
    const onDisplayModeChange = () => setStandalone(isStandaloneMode())
    window.addEventListener('beforeinstallprompt', onInstallPrompt)
    window.addEventListener('appinstalled', onInstalled)
    displayMode.addEventListener?.('change', onDisplayModeChange)
    return () => {
      window.removeEventListener('beforeinstallprompt', onInstallPrompt)
      window.removeEventListener('appinstalled', onInstalled)
      displayMode.removeEventListener?.('change', onDisplayModeChange)
    }
  }, [])

  const install = async () => {
    if (!installPrompt) return
    await installPrompt.prompt()
    const choice = await installPrompt.userChoice
    if (choice.outcome === 'accepted') setInstallPrompt(null)
  }

  return { canInstall: Boolean(installPrompt), install, ios: isIosDevice(), standalone }
}
